import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Upload, FileText, Loader2, CheckCircle2, AlertTriangle, AlertCircle,
  Sparkles, Building2, ShieldCheck, TrendingUp, Trash2,
  History, Banknote, Percent, Calendar, BadgeDollarSign, FileCheck2,
  ScanLine, Lightbulb, FileSignature, Copy, X, Plus, Layers, Download,
} from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const MAX_BYTES = 15 * 1024 * 1024;
const MAX_PAYSLIPS = 2;

const fmtMoney = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
};
const fmtMoneyDecimal = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`);
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const annualiseGross = (gross, frequency) => {
  if (!gross || !frequency) return null;
  const f = String(frequency).toLowerCase();
  if (f.startsWith('week')) return gross * 52;
  if (f.startsWith('fort')) return gross * 26;
  if (f.startsWith('month')) return gross * 12;
  return null;
};

// ---------------------------------------------------------------------------
// Themed sub-components
// ---------------------------------------------------------------------------

const Stat = ({ label, value, accent = 'emerald', icon: Icon }) => (
  <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-xl p-4">
    <div className="flex items-center gap-2 mb-2">
      {Icon && <Icon className={`h-4 w-4 text-${accent}-500 dark:text-${accent}-400`} />}
      <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
    </div>
    <div className={`text-2xl font-bold text-${accent}-600 dark:text-${accent}-300`}>{value}</div>
  </div>
);

const FieldRow = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-3 py-2 border-b border-slate-200 dark:border-slate-800/70 last:border-b-0">
    <span className="text-sm text-slate-600 dark:text-slate-400">{label}</span>
    <span className="font-mono text-sm text-slate-900 dark:text-slate-100 text-right">{children}</span>
  </div>
);

const FlagPill = ({ severity, children }) => {
  const styles = {
    critical: 'bg-rose-50 dark:bg-rose-500/15 border-rose-300 dark:border-rose-500/40 text-rose-700 dark:text-rose-200',
    warn: 'bg-amber-50 dark:bg-amber-500/15 border-amber-300 dark:border-amber-500/40 text-amber-700 dark:text-amber-200',
    info: 'bg-sky-50 dark:bg-sky-500/15 border-sky-300 dark:border-sky-500/40 text-sky-700 dark:text-sky-200',
  }[severity] || 'bg-slate-100 dark:bg-slate-500/15 border-slate-300 dark:border-slate-500/40 text-slate-700 dark:text-slate-200';
  const Icon = severity === 'critical' ? AlertCircle : severity === 'warn' ? AlertTriangle : Lightbulb;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${styles}`}>
      <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
      <div className="text-sm leading-snug">{children}</div>
    </div>
  );
};

const VerdictBadge = ({ verdict, score }) => {
  const cfg = {
    'Likely fit': 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 border-emerald-300 dark:border-emerald-500/40',
    'Possible fit': 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-200 border-amber-300 dark:border-amber-500/40',
    'Likely decline': 'bg-rose-100 dark:bg-rose-500/15 text-rose-700 dark:text-rose-200 border-rose-300 dark:border-rose-500/40',
  }[verdict] || 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600';
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${cfg}`}>
      <span className="text-xs font-semibold">{verdict}</span>
      <span className="text-xs font-mono opacity-80">{score.toFixed(0)}/100</span>
    </div>
  );
};

const LenderCard = ({ match }) => (
  <div data-testid={`lender-card-${match.lender_id}`}
    className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-xl p-4 hover:border-emerald-500/60 dark:hover:border-emerald-600/50 transition-colors shadow-sm">
    <div className="flex items-start justify-between mb-3 gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center text-slate-700 dark:text-white font-bold flex-shrink-0">
          {match.name.charAt(0)}
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">{match.name}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">Max LVR {match.max_lvr}%</div>
        </div>
      </div>
      <VerdictBadge verdict={match.verdict} score={match.score} />
    </div>
    <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">Assessable income</div>
    <div className="text-lg font-bold text-emerald-600 dark:text-emerald-300 mb-3">{fmtMoney(match.assessable_annual_income)}</div>
    {match.reasons.length > 0 && (
      <ul className="mb-2 space-y-1">
        {match.reasons.map((r, i) => (
          <li key={i} className="text-xs text-emerald-700 dark:text-emerald-200/90 flex items-start gap-1.5">
            <CheckCircle2 className="h-3 w-3 mt-0.5 flex-shrink-0 text-emerald-500 dark:text-emerald-400" />
            <span>{r}</span>
          </li>
        ))}
      </ul>
    )}
    {match.cautions.length > 0 && (
      <ul className="space-y-1">
        {match.cautions.map((c, i) => (
          <li key={i} className="text-xs text-amber-700 dark:text-amber-200/90 flex items-start gap-1.5">
            <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0 text-amber-500 dark:text-amber-400" />
            <span>{c}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

// Staged-file preview tile — shown after file is dropped, before "Upload & Analyze" is clicked.
const StagedFileCard = ({ entry, slotIndex, onRemove, busy }) => {
  const isPdf = (entry.file?.type || '').includes('pdf');
  return (
    <Card className="bg-emerald-50/40 dark:bg-emerald-500/5 border-emerald-300 dark:border-emerald-500/30 border-2 border-dashed" data-testid={`staged-card-${slotIndex}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 dark:text-slate-100 truncate text-sm">
                {entry.name}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Slot {slotIndex + 1} · {(entry.size / 1024 / 1024).toFixed(2)} MB · {isPdf ? 'PDF' : 'Image'}
              </div>
            </div>
          </div>
          <Button
            size="sm" variant="ghost"
            onClick={onRemove}
            disabled={busy}
            className="text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 flex-shrink-0"
            data-testid={`staged-remove-${slotIndex}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-100/60 dark:bg-emerald-500/10">
          {busy
            ? <><Loader2 className="w-3 h-3 animate-spin" /> Analysing payslip {slotIndex + 1}…</>
            : <><CheckCircle2 className="w-3 h-3" /> Staged. Click "Upload &amp; Analyze" to process.</>}
        </div>
      </CardContent>
    </Card>
  );
};

// Single-payslip extracted-fields card (shows employer, pay period, gross, etc.)
const PayslipCard = ({ analysis, slotIndex, onRemove }) => {
  const ext = analysis?.extraction || {};
  const annualised = analysis?.annualised_gross
    || annualiseGross(ext.gross_income, ext.pay_frequency);
  return (
    <Card className="bg-white dark:bg-slate-900/40 border-slate-200 dark:border-slate-800" data-testid={`payslip-card-${slotIndex}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileCheck2 className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
              Payslip {slotIndex + 1} — {ext.employer_name || analysis?.filename || 'Unknown'}
            </CardTitle>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {ext.pay_frequency || '—'} · {fmtDate(ext.pay_period_start)} → {fmtDate(ext.pay_period_end)}
            </div>
          </div>
          <Button
            size="sm" variant="ghost"
            onClick={onRemove}
            className="text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10"
            data-testid={`payslip-remove-${slotIndex}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-1">
        <FieldRow label="Gross (pay)">{fmtMoneyDecimal(ext.gross_income)}</FieldRow>
        <FieldRow label="Annualised">{fmtMoney(annualised)}</FieldRow>
        <FieldRow label="Net (pay)">{fmtMoneyDecimal(ext.net_pay)}</FieldRow>
        <FieldRow label="Tax">{fmtMoneyDecimal(ext.tax_withheld)}</FieldRow>
        <FieldRow label="Super">{fmtMoneyDecimal(ext.super_amount)} {ext.super_rate_pct ? `(${fmtPct(ext.super_rate_pct)})` : ''}</FieldRow>
        <FieldRow label="HECS">{fmtMoneyDecimal(ext.hecs)}</FieldRow>
        <FieldRow label="YTD gross">{fmtMoneyDecimal(ext.ytd_gross)}</FieldRow>
        <FieldRow label="Confidence">{ext.confidence !== null && ext.confidence !== undefined ? `${(ext.confidence * 100).toFixed(0)}%` : '—'}</FieldRow>
      </CardContent>
    </Card>
  );
};

// Drag-and-drop tile (used for slots that don't yet have a payslip)
const UploadTile = ({ onFile, busy, label }) => {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      onClick={() => inputRef.current?.click()}
      className={`group cursor-pointer rounded-2xl border-2 border-dashed p-6 sm:p-10 text-center transition-all min-h-[200px] flex items-center justify-center ${
        over
          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/5'
          : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900/40 hover:border-emerald-500/60 dark:hover:border-emerald-600/60 hover:bg-emerald-50/40 dark:hover:bg-slate-900/60'
      }`}
      data-testid="payslip-dropzone"
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp"
        onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
        className="hidden"
        data-testid="payslip-file-input"
      />
      {busy ? (
        <div className="flex flex-col items-center gap-2 py-2">
          <Loader2 className="h-10 w-10 text-emerald-500 dark:text-emerald-400 animate-spin" />
          <div className="font-semibold text-slate-900 dark:text-slate-100">Analysing…</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">Extracting fields & risk flags</div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 flex items-center justify-center group-hover:scale-105 transition-transform">
            <FileText className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="font-semibold text-slate-900 dark:text-slate-100">{label}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">PDF, PNG, JPG or WEBP · max 15 MB</div>
        </div>
      )}
    </div>
  );
};

// Lightweight markdown renderer — handles headings, bold, lists, and paragraphs
// without pulling in extra deps. Good enough for AI-drafted notes.
const renderInline = (text) => {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
};
const SimpleMarkdown = ({ source }) => {
  if (!source) return null;
  const lines = source.split(/\r?\n/);
  const blocks = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length > 0) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="list-disc pl-6 my-2 space-y-1">
          {listBuf.map((l, i) => <li key={i}>{renderInline(l)}</li>)}
        </ul>
      );
      listBuf = [];
    }
  };
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (/^---+$/.test(line)) { flushList(); blocks.push(<hr key={`hr-${idx}`} className="my-3 border-slate-200 dark:border-slate-700" />); return; }
    if (/^# /.test(line)) { flushList(); blocks.push(<h2 key={idx} className="text-xl font-bold mt-4 mb-2">{renderInline(line.slice(2))}</h2>); return; }
    if (/^## /.test(line)) { flushList(); blocks.push(<h3 key={idx} className="text-lg font-bold mt-3 mb-2 text-emerald-600 dark:text-emerald-300">{renderInline(line.slice(3))}</h3>); return; }
    if (/^- /.test(line)) { listBuf.push(line.slice(2)); return; }
    if (line.trim() === '') { flushList(); return; }
    flushList();
    blocks.push(<p key={idx} className="my-2 leading-relaxed">{renderInline(line)}</p>);
  });
  flushList();
  return <div>{blocks}</div>;
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const ReadMyIncome = () => {
  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [uploadingSlot, setUploadingSlot] = useState(null); // index of slot currently uploading
  const [analyses, setAnalyses] = useState([]); // up to 2
  // Staged-files mode: drop files, click "Upload & Analyze" → batch analyse + cross-check
  const [pendingFiles, setPendingFiles] = useState([]); // [{file, name, size, slot}]
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [averageData, setAverageData] = useState(null);
  const [averaging, setAveraging] = useState(false);

  const [manualProfile, setManualProfile] = useState({
    employment_type: 'PAYG',
    annual_gross_income: '',
    employment_months: 12,
    on_probation: false,
    overtime_pct_of_base: 0,
    commission_pct_of_base: 0,
    bonus_pct_of_base: 0,
    desired_loan_amount: '',
    property_value: '',
  });
  const [matches, setMatches] = useState([]);
  const [matching, setMatching] = useState(false);

  // Broker note modal
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteForm, setNoteForm] = useState({
    client_name: '', loan_purpose: '', desired_loan_amount: '', property_value: '', extra_context: '',
  });
  const [noteSelectedLenders, setNoteSelectedLenders] = useState([]);

  // ----- History -----
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/read-my-income/payslips`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.items || []);
      }
    } catch (e) {
      console.error('history failed', e);
    } finally {
      setHistoryLoading(false);
    }
  }, [token]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // ----- Upload + Analyse -----
  const validateFile = (f) => {
    if (!ALLOWED_TYPES.includes(f.type)) {
      toast.error('Unsupported file type. Use PDF, PNG, JPG or WEBP.');
      return false;
    }
    if (f.size > MAX_BYTES) {
      toast.error(`File too large (${(f.size / 1024 / 1024).toFixed(1)} MB). Max 15 MB.`);
      return false;
    }
    return true;
  };

  // Stage a file locally — does NOT upload yet. User must click "Upload & Analyze".
  const stagePayslip = (file, targetSlot) => {
    if (!validateFile(file)) return;
    setPendingFiles((prev) => {
      const next = [...prev];
      next[targetSlot] = { file, name: file.name, size: file.size };
      return next;
    });
  };

  const removePending = (idx) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // Send a single file → backend → returns analysis dict
  const uploadOne = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('save', 'true');
    const res = await fetch(`${backendUrl}/api/read-my-income/payslip/analyze`, {
      method: 'POST',
      headers: authHeaders,
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Analysis failed.');
    }
    return res.json();
  };

  // Bulk-analyze — uploads all pending files, then cross-checks via /average endpoint
  const analyzeAllPending = async () => {
    const filesToProcess = pendingFiles.filter(Boolean);
    if (filesToProcess.length === 0) {
      toast.error('Add at least one payslip first.');
      return;
    }
    setBulkAnalyzing(true);
    const newAnalyses = [...analyses];
    try {
      // Upload sequentially so the backend doesn't get hit with 2 simultaneous Gemini calls
      for (let i = 0; i < pendingFiles.length; i++) {
        const slot = i;
        const entry = pendingFiles[slot];
        if (!entry) continue;
        setUploadingSlot(slot);
        try {
          const data = await uploadOne(entry.file);
          newAnalyses[slot] = data;
          // Optimistically render after each upload
          setAnalyses(newAnalyses.filter(Boolean));
          toast.success(`Payslip ${slot + 1} analysed (${(data.extraction?.confidence * 100 || 0).toFixed(0)}% confidence)`);
        } catch (e) {
          toast.error(`Payslip ${slot + 1} failed: ${e.message}`);
        }
      }
      setUploadingSlot(null);
      setPendingFiles([]); // Clear staged files
      await loadHistory();
      // Cross-check + average runs automatically via the analyses-change effect below
      if (filesToProcess.length === 2) {
        toast.success('Cross-check complete — see Lender Income panel below', { duration: 4000 });
      }
    } finally {
      setBulkAnalyzing(false);
      setUploadingSlot(null);
    }
  };

  // Backwards-compat: keep the same function name used by older call sites (just stages now).
  const analyzePayslip = (file, targetSlot) => stagePayslip(file, targetSlot);

  const removeAnalysis = (idx) => {
    setAnalyses((prev) => prev.filter((_, i) => i !== idx));
  };

  // Recompute average + auto-prefill manual profile + lender match whenever analyses change
  useEffect(() => {
    if (analyses.length === 0) {
      setAverageData(null);
      return;
    }
    const ids = analyses.map((a) => a.id).filter(Boolean);
    if (ids.length === 0) return;

    let cancelled = false;
    (async () => {
      setAveraging(true);
      try {
        const res = await fetch(`${backendUrl}/api/read-my-income/payslips/average`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ payslip_ids: ids }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setAverageData(data);

        // Sync manual profile + auto-run lender match
        const ann = data.recommended_for_lender_match || data.simple_avg_annual_gross;
        const np = data.non_base_pct || {};
        setManualProfile((p) => ({
          ...p,
          annual_gross_income: Math.round(ann).toString(),
          overtime_pct_of_base: np.overtime ?? 0,
          bonus_pct_of_base: np.bonus ?? 0,
          commission_pct_of_base: np.commission ?? 0,
          employment_type: data.employment_type === 'Contractor' ? 'Self-Employed' : 'PAYG',
        }));
        await runLenderMatch({
          employment_type: data.employment_type === 'Contractor' ? 'Self-Employed' : 'PAYG',
          annual_gross_income: ann,
          employment_months: 12,
          on_probation: false,
          overtime_pct_of_base: np.overtime ?? 0,
          bonus_pct_of_base: np.bonus ?? 0,
          commission_pct_of_base: np.commission ?? 0,
        });
      } finally {
        if (!cancelled) setAveraging(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [analyses.map((a) => a?.id).join('|')]);

  const runLenderMatch = async (profile) => {
    setMatching(true);
    try {
      const res = await fetch(`${backendUrl}/api/read-my-income/lender-match`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(profile),
      });
      if (!res.ok) {
        toast.error('Lender match failed');
        return;
      }
      const data = await res.json();
      setMatches(data.matches || []);
    } finally {
      setMatching(false);
    }
  };

  const onManualMatch = async () => {
    const gross = parseFloat(manualProfile.annual_gross_income);
    if (!gross || gross <= 0) {
      toast.error('Enter a valid annual gross income');
      return;
    }
    const profile = {
      employment_type: manualProfile.employment_type,
      annual_gross_income: gross,
      employment_months: parseInt(manualProfile.employment_months) || 0,
      on_probation: !!manualProfile.on_probation,
      overtime_pct_of_base: parseFloat(manualProfile.overtime_pct_of_base) || 0,
      commission_pct_of_base: parseFloat(manualProfile.commission_pct_of_base) || 0,
      bonus_pct_of_base: parseFloat(manualProfile.bonus_pct_of_base) || 0,
      desired_loan_amount: manualProfile.desired_loan_amount ? parseFloat(manualProfile.desired_loan_amount) : null,
      property_value: manualProfile.property_value ? parseFloat(manualProfile.property_value) : null,
    };
    await runLenderMatch(profile);
  };

  const onDelete = async (id) => {
    try {
      const res = await fetch(`${backendUrl}/api/read-my-income/payslips/${id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (res.ok) {
        toast.success('Analysis deleted');
        setAnalyses((prev) => prev.filter((a) => a.id !== id));
        await loadHistory();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const [downloadingReport, setDownloadingReport] = useState(false);
  const downloadReport = async () => {
    if (analyses.length === 0) {
      toast.error('Upload at least one payslip first.');
      return;
    }
    setDownloadingReport(true);
    try {
      const body = {
        payslip_ids: analyses.map((a) => a.id).filter(Boolean),
        client_name: noteForm.client_name || null,
        // Include any selected lender shortlist if present
        selected_lender_ids: noteSelectedLenders.length > 0 ? noteSelectedLenders : null,
      };
      const res = await fetch(`${backendUrl}/api/read-my-income/download-report`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not generate report.');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (noteForm.client_name || 'income-report')
        .replace(/[^a-zA-Z0-9-_]+/g, '-').replace(/-+/g, '-');
      a.download = `${safeName}-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (e) {
      console.error(e);
      toast.error('Could not generate report.');
    } finally {
      setDownloadingReport(false);
    }
  };

  const onLoadFromHistory = async (id, targetSlot) => {
    try {
      const res = await fetch(`${backendUrl}/api/read-my-income/payslips/${id}`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setAnalyses((prev) => {
          const next = [...prev];
          next[targetSlot] = data;
          return next.filter(Boolean);
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Broker note generation
  const openNoteModal = () => {
    if (analyses.length === 0) {
      toast.error('Upload at least one payslip first.');
      return;
    }
    // Sensible defaults from manual profile
    setNoteForm((f) => ({
      ...f,
      desired_loan_amount: manualProfile.desired_loan_amount || f.desired_loan_amount,
      property_value: manualProfile.property_value || f.property_value,
    }));
    // Pre-select top 2 lenders by score
    const top = matches.slice(0, 2).map((m) => m.lender_id);
    setNoteSelectedLenders(top);
    setNoteOpen(true);
    setNoteContent('');
  };

  const generateNote = async () => {
    setNoteLoading(true);
    try {
      const body = {
        payslip_ids: analyses.map((a) => a.id).filter(Boolean),
        selected_lender_ids: noteSelectedLenders,
        client_name: noteForm.client_name || null,
        loan_purpose: noteForm.loan_purpose || null,
        desired_loan_amount: noteForm.desired_loan_amount ? parseFloat(noteForm.desired_loan_amount) : null,
        property_value: noteForm.property_value ? parseFloat(noteForm.property_value) : null,
        extra_context: noteForm.extra_context || null,
      };
      const res = await fetch(`${backendUrl}/api/read-my-income/generate-broker-note`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Note generation failed.');
        return;
      }
      const data = await res.json();
      setNoteContent(data.note_markdown || '');
    } catch (e) {
      console.error(e);
      toast.error('Note generation failed.');
    } finally {
      setNoteLoading(false);
    }
  };

  const copyNote = async () => {
    try {
      await navigator.clipboard.writeText(noteContent);
      toast.success('Note copied to clipboard');
    } catch {
      toast.error('Could not copy');
    }
  };

  const toggleLender = (id) => {
    setNoteSelectedLenders((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Combined risk flags across all loaded analyses
  const allFlags = analyses.flatMap((a, i) =>
    (a?.flags || []).map((f) => ({ ...f, _idx: i }))
  );

  const canAddMore = analyses.length < MAX_PAYSLIPS;

  return (
    <div className="min-h-screen flex flex-col bg-page text-text-main pb-24 md:pb-0 transition-colors">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-10 space-y-8" data-testid="read-my-income-page">

        {/* Hero */}
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-xs font-medium">
            <Sparkles className="h-3 w-3" /> AI Income Verification & Lender Matching
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Read <span className="bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 dark:from-emerald-300 dark:via-teal-300 dark:to-emerald-400 bg-clip-text text-transparent">My Income</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 max-w-3xl">
            Drop up to two PAYG payslips, get an AI-extracted income breakdown, automated risk flags, lender-conservative averaging, and a one-click compliance broker note.
          </p>
        </div>

        {/* Feature pills */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { icon: ScanLine, label: 'PAYG Analyser' },
            { icon: ShieldCheck, label: 'Smart Risk Flags' },
            { icon: Building2, label: 'Lender Matcher' },
            { icon: Layers, label: 'Multi-Payslip Avg' },
            { icon: Banknote, label: 'YTD Calculator' },
            { icon: FileSignature, label: 'Broker Note AI' },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm">
              <f.icon className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
              <span className="text-xs text-slate-700 dark:text-slate-300">{f.label}</span>
            </div>
          ))}
        </div>

        {/* Upload + payslip slots */}
        <Card className="bg-white dark:bg-slate-900/40 border-slate-200 dark:border-slate-800">
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-emerald-500 dark:text-emerald-400" />
                Payslips
                <Badge variant="outline" className="ml-2 border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400">
                  {analyses.length + pendingFiles.filter(Boolean).length}/{MAX_PAYSLIPS}
                </Badge>
              </CardTitle>
              <Button
                onClick={openNoteModal}
                disabled={analyses.length === 0}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                data-testid="open-broker-note-btn"
              >
                <FileSignature className="h-4 w-4 mr-2" />
                Generate broker note
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Slot 0 */}
              {analyses[0] ? (
                <PayslipCard analysis={analyses[0]} slotIndex={0} onRemove={() => removeAnalysis(0)} />
              ) : pendingFiles[0] ? (
                <StagedFileCard
                  entry={pendingFiles[0]} slotIndex={0}
                  onRemove={() => removePending(0)}
                  busy={bulkAnalyzing && uploadingSlot === 0}
                />
              ) : (
                <UploadTile
                  onFile={(f) => stagePayslip(f, 0)}
                  busy={false}
                  label="Drop your primary payslip"
                />
              )}
              {/* Slot 1 */}
              {analyses[1] ? (
                <PayslipCard analysis={analyses[1]} slotIndex={1} onRemove={() => removeAnalysis(1)} />
              ) : pendingFiles[1] ? (
                <StagedFileCard
                  entry={pendingFiles[1]} slotIndex={1}
                  onRemove={() => removePending(1)}
                  busy={bulkAnalyzing && uploadingSlot === 1}
                />
              ) : canAddMore ? (
                <UploadTile
                  onFile={(f) => {
                    // Determine the next free slot (0 or 1)
                    const slot = analyses[0] || pendingFiles[0] ? 1 : 0;
                    stagePayslip(f, slot);
                  }}
                  busy={false}
                  label={(analyses.length + pendingFiles.filter(Boolean).length) === 0
                    ? 'Or drop a second payslip (optional)'
                    : 'Add a second payslip for averaging'}
                />
              ) : null}
            </div>

            {/* Big primary CTA — only when there are staged files awaiting analysis */}
            {pendingFiles.filter(Boolean).length > 0 && (
              <div className="rounded-xl border-2 border-emerald-300 dark:border-emerald-500/40 bg-emerald-50/70 dark:bg-emerald-500/5 p-4 sm:p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="upload-analyze-banner">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    <span className="font-semibold text-emerald-700 dark:text-emerald-200">
                      {pendingFiles.filter(Boolean).length} payslip{pendingFiles.filter(Boolean).length === 1 ? '' : 's'} ready to analyse
                    </span>
                  </div>
                  <p className="text-xs text-emerald-700/80 dark:text-emerald-300/80 mt-1">
                    {pendingFiles.filter(Boolean).length === 2
                      ? <>Click <strong>Upload &amp; Analyze</strong> — Gemini Pro will read both payslips, cross-check the figures, and calculate the income lenders will use.</>
                      : <>Click <strong>Upload &amp; Analyze</strong> to extract income, run risk flags and compute lender-assessable figures. Add a 2nd payslip for cross-check.</>}
                  </p>
                </div>
                <Button
                  size="lg"
                  onClick={analyzeAllPending}
                  disabled={bulkAnalyzing}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white text-base font-semibold px-6 shadow-lg shadow-emerald-500/30"
                  data-testid="upload-analyze-btn"
                >
                  {bulkAnalyzing
                    ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Analysing…</>
                    : <><Upload className="w-5 h-5 mr-2" /> Upload &amp; Analyze</>}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Average panel — only meaningful with ≥1 payslips */}
        {averageData && (
          <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-emerald-200 dark:border-emerald-800/40" data-testid="average-panel">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Layers className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                {averageData.count > 1 ? 'Cross-check & Lender Income' : 'Lender Income'}
                {averaging && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Hero: the single number that matters — what lenders will use */}
              <div className="rounded-2xl bg-white dark:bg-slate-900/60 border-2 border-emerald-300 dark:border-emerald-500/50 p-5 sm:p-6 mb-5" data-testid="lender-income-hero">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wider font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 mb-1">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      Income lenders will use {averageData.count > 1 ? `(cross-checked from ${averageData.count} payslips)` : ''}
                    </div>
                    <div className="text-4xl sm:text-5xl font-bold text-emerald-700 dark:text-emerald-300 tracking-tight tabular-nums">
                      {fmtMoney(averageData.recommended_for_lender_match)}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                      annual gross · per AU lender-conservative methodology
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {averageData.count > 1 && (
                      <div className="text-right">
                        <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">Income spread</div>
                        <div className={`text-2xl font-bold tabular-nums ${(averageData.spread_pct || 0) > 10 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                          {fmtPct(averageData.spread_pct)}
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                          {(averageData.spread_pct || 0) > 10 ? 'variable income' : 'stable income'}
                        </div>
                      </div>
                    )}
                    <Button
                      size="sm"
                      onClick={downloadReport}
                      disabled={downloadingReport}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-md"
                      data-testid="download-report-btn"
                    >
                      {downloadingReport
                        ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Building PDF…</>
                        : <><Download className="w-4 h-4 mr-1.5" /> Download report</>}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-2">
                How we got there
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Stat label="Simple average" value={fmtMoney(averageData.simple_avg_annual_gross)} accent="emerald" icon={TrendingUp} />
                <Stat label="Lower-of" value={fmtMoney(averageData.lower_of_annual_gross)} accent="teal" icon={Banknote} />
                <Stat label="YTD extrapolated" value={fmtMoney(averageData.ytd_extrapolated_annual_gross)} accent="sky" icon={Calendar} />
                <Stat label="Recommended" value={fmtMoney(averageData.recommended_for_lender_match)} accent="emerald" icon={ShieldCheck} />
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-slate-600 dark:text-slate-400">
                <div className="bg-white/70 dark:bg-slate-900/40 rounded-lg p-3 border border-slate-200 dark:border-slate-800">
                  <div className="font-semibold text-slate-700 dark:text-slate-300 mb-1">Method</div>
                  Lenders typically take the lower of "current annualised" vs "YTD extrapolated to full year". The <strong>Recommended</strong> figure applies that policy.
                </div>
                <div className="bg-white/70 dark:bg-slate-900/40 rounded-lg p-3 border border-slate-200 dark:border-slate-800">
                  <div className="font-semibold text-slate-700 dark:text-slate-300 mb-1">Income spread</div>
                  Across {averageData.count} payslip{averageData.count > 1 ? 's' : ''} the variance is {fmtPct(averageData.spread_pct)}. {averageData.spread_pct > 10 ? 'Variable income — expect haircuts.' : 'Stable income.'}
                </div>
                <div className="bg-white/70 dark:bg-slate-900/40 rounded-lg p-3 border border-slate-200 dark:border-slate-800">
                  <div className="font-semibold text-slate-700 dark:text-slate-300 mb-1">Non-base mix</div>
                  Overtime {fmtPct((averageData.non_base_pct?.overtime || 0) * 100)} ·
                  Bonus {fmtPct((averageData.non_base_pct?.bonus || 0) * 100)} ·
                  Commission {fmtPct((averageData.non_base_pct?.commission || 0) * 100)}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Risk flags (combined) */}
        {analyses.length > 0 && (
          <Card className="bg-white dark:bg-slate-900/40 border-slate-200 dark:border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-5 w-5 text-amber-500 dark:text-amber-400" />
                Risk flags
                <Badge variant="outline" className="ml-2 border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400">{allFlags.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {allFlags.length === 0 ? (
                <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 rounded-lg p-3 text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="h-5 w-5" />
                  <div>
                    <div className="font-medium">No red flags detected</div>
                    <div className="text-xs opacity-80">All sanity checks passed across {analyses.length} payslip{analyses.length > 1 ? 's' : ''}. Always cross-reference against the originals.</div>
                  </div>
                </div>
              ) : (
                allFlags.map((f, i) => (
                  <FlagPill key={i} severity={f.severity}>
                    <span className="text-xs uppercase tracking-wide opacity-70 mr-2">Payslip {f._idx + 1}</span>
                    {f.message}
                  </FlagPill>
                ))
              )}
            </CardContent>
          </Card>
        )}

        {/* Lender matcher */}
        <Card className="bg-white dark:bg-slate-900/40 border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-5 w-5 text-emerald-500 dark:text-emerald-400" /> Lender policy match
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Manual profile inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs text-slate-500 dark:text-slate-400">Employment type</Label>
                <select
                  value={manualProfile.employment_type}
                  onChange={(e) => setManualProfile((p) => ({ ...p, employment_type: e.target.value }))}
                  className="mt-1 w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-md px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500"
                  data-testid="manual-employment-type"
                >
                  <option value="PAYG">PAYG</option>
                  <option value="Self-Employed">Self-Employed</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 dark:text-slate-400">Annual gross income (AUD)</Label>
                <Input type="number" value={manualProfile.annual_gross_income}
                  onChange={(e) => setManualProfile((p) => ({ ...p, annual_gross_income: e.target.value }))}
                  placeholder="e.g. 120000"
                  className="mt-1 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700"
                  data-testid="manual-gross-income" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 dark:text-slate-400">Employment months</Label>
                <Input type="number" value={manualProfile.employment_months}
                  onChange={(e) => setManualProfile((p) => ({ ...p, employment_months: e.target.value }))}
                  className="mt-1 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700"
                  data-testid="manual-employment-months" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 dark:text-slate-400">Overtime % of gross</Label>
                <Input type="number" step="0.01" min="0" max="1" value={manualProfile.overtime_pct_of_base}
                  onChange={(e) => setManualProfile((p) => ({ ...p, overtime_pct_of_base: e.target.value }))}
                  className="mt-1 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 dark:text-slate-400">Bonus % of gross</Label>
                <Input type="number" step="0.01" min="0" max="1" value={manualProfile.bonus_pct_of_base}
                  onChange={(e) => setManualProfile((p) => ({ ...p, bonus_pct_of_base: e.target.value }))}
                  className="mt-1 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 dark:text-slate-400">Commission % of gross</Label>
                <Input type="number" step="0.01" min="0" max="1" value={manualProfile.commission_pct_of_base}
                  onChange={(e) => setManualProfile((p) => ({ ...p, commission_pct_of_base: e.target.value }))}
                  className="mt-1 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 dark:text-slate-400">Desired loan (AUD, optional)</Label>
                <Input type="number" value={manualProfile.desired_loan_amount}
                  onChange={(e) => setManualProfile((p) => ({ ...p, desired_loan_amount: e.target.value }))}
                  placeholder="e.g. 600000"
                  className="mt-1 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 dark:text-slate-400">Property value (AUD, optional)</Label>
                <Input type="number" value={manualProfile.property_value}
                  onChange={(e) => setManualProfile((p) => ({ ...p, property_value: e.target.value }))}
                  placeholder="e.g. 750000"
                  className="mt-1 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700" />
              </div>
              <div className="flex items-center gap-2 mt-6">
                <input id="probation" type="checkbox" checked={!!manualProfile.on_probation}
                  onChange={(e) => setManualProfile((p) => ({ ...p, on_probation: e.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900" />
                <Label htmlFor="probation" className="text-sm text-slate-700 dark:text-slate-300">Currently on probation</Label>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-slate-500 dark:text-slate-500">
                Top 10 AU lenders ranked by fit. Haircuts on overtime, bonus and commission applied per published lender policy stubs.
              </div>
              <Button onClick={onManualMatch} disabled={matching}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="run-lender-match-btn">
                {matching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Building2 className="h-4 w-4 mr-2" />}
                Match lenders
              </Button>
            </div>

            {matches.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {matches.map((m) => <LenderCard key={m.lender_id} match={m} />)}
              </div>
            )}
          </CardContent>
        </Card>

        {/* History */}
        <Card className="bg-white dark:bg-slate-900/40 border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="h-5 w-5 text-slate-500 dark:text-slate-400" /> Recent analyses
              <Badge variant="outline" className="ml-2 border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400">{history.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex items-center justify-center py-6 text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
              </div>
            ) : history.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6">No payslips analysed yet.</div>
            ) : (
              <div className="space-y-2">
                {history.map((h) => (
                  <div key={h.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 hover:border-emerald-500/40 dark:hover:border-emerald-700/40 transition-colors">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{h.extraction?.employer_name || h.filename}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-500 truncate">
                        {h.filename} · {fmtDate(h.created_at)} · annualised {fmtMoney(h.annualised_gross)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button size="sm" variant="outline"
                        onClick={() => onLoadFromHistory(h.id, analyses.length < MAX_PAYSLIPS ? analyses.length : 0)}
                        className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                        disabled={analyses.length >= MAX_PAYSLIPS && !analyses.some((a) => a.id === h.id)}
                        data-testid={`history-load-${h.id}`}>
                        Load
                      </Button>
                      <Button size="sm" variant="ghost"
                        onClick={() => onDelete(h.id)}
                        className="text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10"
                        data-testid={`history-delete-${h.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />

      {/* Broker Note Modal */}
      {noteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-testid="broker-note-modal">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <FileSignature className="h-5 w-5 text-emerald-500 dark:text-emerald-400" />
                <h2 className="text-lg font-semibold">Generate broker note</h2>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setNoteOpen(false)} data-testid="close-broker-note-btn">
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {!noteContent ? (
                <>
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    Pulls from {analyses.length} payslip{analyses.length > 1 ? 's' : ''}, the lender shortlist below, and your client context. Powered by Claude Sonnet 4.5.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Client name</Label>
                      <Input value={noteForm.client_name}
                        onChange={(e) => setNoteForm((f) => ({ ...f, client_name: e.target.value }))}
                        placeholder="e.g. John & Mary Smith"
                        className="mt-1" data-testid="note-client-name" />
                    </div>
                    <div>
                      <Label className="text-xs">Loan purpose</Label>
                      <Input value={noteForm.loan_purpose}
                        onChange={(e) => setNoteForm((f) => ({ ...f, loan_purpose: e.target.value }))}
                        placeholder="e.g. Owner-occupied purchase"
                        className="mt-1" data-testid="note-loan-purpose" />
                    </div>
                    <div>
                      <Label className="text-xs">Desired loan (AUD)</Label>
                      <Input type="number" value={noteForm.desired_loan_amount}
                        onChange={(e) => setNoteForm((f) => ({ ...f, desired_loan_amount: e.target.value }))}
                        placeholder="600000" className="mt-1" data-testid="note-loan-amount" />
                    </div>
                    <div>
                      <Label className="text-xs">Property value (AUD)</Label>
                      <Input type="number" value={noteForm.property_value}
                        onChange={(e) => setNoteForm((f) => ({ ...f, property_value: e.target.value }))}
                        placeholder="750000" className="mt-1" data-testid="note-property-value" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Extra context (optional)</Label>
                    <Input value={noteForm.extra_context}
                      onChange={(e) => setNoteForm((f) => ({ ...f, extra_context: e.target.value }))}
                      placeholder="e.g. First-home buyer, will use FHB grant"
                      className="mt-1" data-testid="note-extra-context" />
                  </div>
                  <div>
                    <Label className="text-xs">Shortlist lenders to mention by name</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                      {matches.length === 0 ? (
                        <div className="text-xs text-slate-500 col-span-full">Run a lender match first to populate this list.</div>
                      ) : matches.map((m) => (
                        <button
                          key={m.lender_id}
                          onClick={() => toggleLender(m.lender_id)}
                          className={`text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
                            noteSelectedLenders.includes(m.lender_id)
                              ? 'bg-emerald-50 dark:bg-emerald-500/15 border-emerald-400 dark:border-emerald-500 text-emerald-800 dark:text-emerald-200'
                              : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-emerald-400'
                          }`}
                          data-testid={`note-lender-${m.lender_id}`}
                        >
                          <div className="font-medium">{m.name}</div>
                          <div className="opacity-70 text-[10px] mt-0.5">{m.verdict} · {m.score.toFixed(0)}/100</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="prose prose-sm dark:prose-invert max-w-none text-slate-800 dark:text-slate-200" data-testid="broker-note-output">
                  <SimpleMarkdown source={noteContent} />
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-800">
              {!noteContent ? (
                <>
                  <Button variant="outline" onClick={() => setNoteOpen(false)}>Cancel</Button>
                  <Button onClick={generateNote} disabled={noteLoading}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                    data-testid="generate-note-btn">
                    {noteLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    Generate
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setNoteContent('')} data-testid="regenerate-note-btn">
                    <Sparkles className="h-4 w-4 mr-2" /> Regenerate
                  </Button>
                  <Button variant="outline" onClick={copyNote} data-testid="copy-note-btn">
                    <Copy className="h-4 w-4 mr-2" /> Copy
                  </Button>
                  <Button onClick={() => setNoteOpen(false)}>Close</Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadMyIncome;
